import { Box, Skeleton, Stack } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type React from 'react';
import { createElement, useEffect, useMemo, useState } from 'react';
import { MdFullscreen, MdFullscreenExit, MdTableChart } from 'react-icons/md';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DownloadIcon, HorizontalBarsIcon } from '@/assets/icons';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText/FaceliftText';
import FaceliftDatePicker, { type DateRange } from '@/components/Molecules/FaceliftDatePicker';
import { ProfileMenu } from '@/components/Molecules/ProfileMenu';
import Show from '@/components/Molecules/Show';
import type { TableData } from '@/components/Organisms/ChartWithTableView';
import ChartWithTableView from '@/components/Organisms/ChartWithTableView';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import { useGetDailyCollectionTrend } from '@/hooks/service-hooks/collection';
import { useFullscreen } from '@/hooks/useFullscreen';
import { formatChartNumber } from '@/utils';
import { calculateChartDateRange, chartDateFilterOptions, DEFAULT_CHART_DATE_FILTER } from '@/utils/dateFilters';
import { generateCSVFromTable, generateExcelFromTable } from '@/utils/exportFiles';
import type { ProfileMenuItem } from '@/utils/profileMenuItems';
import type { DailyCollectionBarChartProps } from './DailyCollectionBarChart.types';

dayjs.extend(utc);
dayjs.extend(timezone);

interface TooltipPayloadItem {
	name: string;
	value: number;
	color: string;
}

interface CustomTooltipProps {
	active?: boolean;
	payload?: TooltipPayloadItem[];
	label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
	if (!active || !payload || payload.length === 0) return null;

	return (
		<Box
			p={3}
			borderRadius="md"
			borderWidth="1px"
			boxShadow="lg"
			_dark={{ bg: 'darkModeBgColor', borderColor: 'gray.600' }}
			_light={{ bg: 'white', borderColor: 'gray.200' }}
		>
			<FaceliftText
				fontSize="sm"
				fontWeight="semibold"
				mb={2}
				_dark={{ color: 'white' }}
				_light={{ color: 'gray.800' }}
			>
				{label}
			</FaceliftText>
			<Stack gap={1}>
				{payload.map((entry) => (
					<Stack key={`${label}-${entry.name}`} direction="row" align="center" gap={2}>
						<svg width="8" height="8">
							<defs>
								<linearGradient id="daily-tooltip-gradient" x1="0" y1="0" x2="1" y2="0">
									<stop offset="0%" stopColor={colors.primary} stopOpacity={1} />
									<stop offset="100%" stopColor={colors.success} stopOpacity={1} />
								</linearGradient>
							</defs>
							<circle cx="4" cy="4" r="4" fill="url(#daily-tooltip-gradient)" />
						</svg>
						<FaceliftText fontSize="xs" _dark={{ color: 'gray.300' }} _light={{ color: 'gray.600' }}>
							${entry.value?.toLocaleString()}
						</FaceliftText>
					</Stack>
				))}
			</Stack>
		</Box>
	);
};

const DailyCollectionBarChart: React.FC<DailyCollectionBarChartProps> = ({
	title = '',
	data: propData,
	isLoading: propIsLoading = false,
	width = 1100,
	height = 500,
	disableScroll = false,
	showFilters = false,
	showTableMenu = false,
	clinicsProductsFilter = '61aea445-e3b4-ea11-a816-000d3a191041|;',
	...rest
}) => {
	const { colorMode } = useColorMode();
	const isDark = colorMode === 'dark';

	// State for menu and table modal
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isTableModalOpen, setIsTableModalOpen] = useState(false);

	// State for disabled amount ranges (legend interactivity)
	const [disabledRanges, setDisabledRanges] = useState<Set<string>>(new Set());

	// State for custom tooltip with position
	const [tooltipState, setTooltipState] = useState<{
		show: boolean;
		x: number;
		y: number;
		name: string;
		value: number;
	} | null>(null);

	// Fullscreen hook
	const { chartContainerRef, isFullScreen, handleFullScreen } = useFullscreen();

	// Handle legend click to toggle range visibility
	const handleLegendClick = (rangeName: string) => {
		setDisabledRanges((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(rangeName)) {
				newSet.delete(rangeName);
			} else {
				newSet.add(rangeName);
			}
			return newSet;
		});
	};

	// Handle bar mouse enter - capture position from event
	const handleBarMouseEnter = (data: any, index: number, event: React.MouseEvent) => {
		const target = event.currentTarget as HTMLElement;
		const rect = target.getBoundingClientRect();
		const containerRect = chartContainerRef.current?.getBoundingClientRect();

		if (containerRect) {
			setTooltipState({
				show: true,
				x: rect.left + rect.width / 2 - containerRect.left,
				y: rect.top - containerRect.top - 10,
				name: data.name,
				value: data.value,
			});
		}
	};

	// Handle bar mouse leave
	const handleBarMouseLeave = () => {
		setTooltipState(null);
	};

	// Handle chart background mouse move to hide tooltip when not over bars
	const handleChartMouseMove = (e: React.MouseEvent) => {
		// Check if the mouse is over a bar element
		const target = e.target as HTMLElement;
		const isOverBar = target.tagName === 'path' || target.closest('.recharts-bar-rectangle');

		// If not over a bar and tooltip is showing, hide it
		if (!isOverBar && tooltipState?.show) {
			setTooltipState(null);
		}
	};

	// State for selected date filter (default to Year to Date)
	const [selectedFilter, setSelectedFilter] = useState<SelectItem | null>(DEFAULT_CHART_DATE_FILTER);
	const [isDateFilterModalOpen, setIsDateFilterModalOpen] = useState<boolean>(false);
	const [customDateRange, setCustomDateRange] = useState<DateRange>({
		startDate: '',
		endDate: '',
	});

	const dateRangeFilter = selectedFilter ? calculateChartDateRange(selectedFilter.value as string) : null

	// Calculate date range based on selected filter
	const [dateRange, setDateRange] = useState<{ start: string; end: string }>(
		dateRangeFilter ? dateRangeFilter : { start: '', end: '' }
	);

	// Fetch data from API when showFilters is true
	const { data: apiResponse, isFetching } = useGetDailyCollectionTrend(
		{
			clinicsProductsFilter,
			multi_filter: '',
			date_filter_range: `${dateRange.start},${dateRange.end}`,
		},
		showFilters,
	);

	// Use API data if showFilters is true, otherwise use propData
	const chartData = showFilters && apiResponse?.data ? apiResponse.data : propData;
	const isLoading = showFilters ? isFetching : propIsLoading;

	// Handle filter change
	const handleFilterChange = (newValue: SingleValue<SelectItem>) => {
		if (newValue) {
			setSelectedFilter(newValue);
			// If custom is selected, open the modal (useEffect will also handle this)
			if (newValue.value === 'custom') {
				setIsDateFilterModalOpen(true);
			}
		}
	};

	// Watch for selectedFilter changes to handle date range updates
	useEffect(() => {
		if (selectedFilter?.value === 'custom') {
			setIsDateFilterModalOpen(true);
		} else if (selectedFilter?.value) {
			const range = selectedFilter.value ? calculateChartDateRange(String(selectedFilter.value)) : null;
			setDateRange(range || { start: '', end: '' });
		}
	}, [selectedFilter]);

	// Export functions
	const exportAsCSV = () => {
		const columns = [
			{ key: 'name', label: 'Date' },
			{ key: 'value', label: 'Collection Amount' },
		];
		generateCSVFromTable(columns, transformedData, 'DailyCollectionTrends', true);
	};

	const exportAsExcel = () => {
		const columns = [
			{ key: 'name', label: 'Date' },
			{ key: 'value', label: 'Collection Amount' },
		];
		generateExcelFromTable(columns, transformedData, 'DailyCollectionTrends', true);
	};

	// Handle menu item click
	const handleMenuNavigate = (href?: string) => {
		if (href === '#view-table') {
			setIsTableModalOpen(true);
		} else if (href === '#view-fullscreen') {
			handleFullScreen();
		} else if (href === '#download-csv') {
			exportAsCSV();
		} else if (href === '#download-xlsx') {
			exportAsExcel();
		}
	};

	// Chart options menu items
	const chartMenuItems: ProfileMenuItem[] = useMemo(() => [
		{
			title: 'View Table',
			icon: createElement(MdTableChart, { width: 18, height: 18 }),
			href: '#view-table',
		},
		{
			title: 'View in full screen',
			icon: createElement(MdFullscreen, { width: 18, height: 18 }),
			href: '#view-fullscreen',
		},
		{
			title: 'Download as CSV',
			icon: createElement(DownloadIcon, { width: 18, height: 18 }),
			href: '#download-csv',
		},
		{
			title: 'Download as XLSX',
			icon: createElement(DownloadIcon, { width: 18, height: 18 }),
			href: '#download-xlsx',
		},
	], []);

	// Transform data into format expected by recharts
	const { transformedData, xDomain, xTicks, hasData, dataLength } = useMemo(() => {
		if (!chartData || !chartData.name || !chartData.value || chartData.name.length === 0) {
			return {
				transformedData: [],
				xDomain: [0, 'auto'] as [number, string],
				xTicks: [] as number[],
				hasData: false,
				dataLength: 0,
			};
		}

		// Transform parallel arrays into object array
		const transformedChartData = chartData.name.map((name, index) => ({
			name,
			value: chartData.value[index] || 0,
		}));

		// Calculate max value for X-axis
		const maxValue = Math.max(...chartData.value);

		// Dynamic step chooser based on max value
		const pickStep = (max: number): number => {
			if (max === 0) return 100;
			if (max <= 100) return 25;
			if (max <= 500) return 100;
			if (max <= 1000) return 250;
			if (max <= 5000) return 1000;
			if (max <= 10000) return 2500;
			if (max <= 50000) return 10000;
			if (max <= 100000) return 25000;
			// For very large numbers
			const magnitude = 10 ** Math.floor(Math.log10(max));
			return Math.ceil(max / (4 * magnitude)) * magnitude;
		};

		// Add 10% padding to max value for better visualization
		const stepBase = maxValue * 1.1;
		const step = pickStep(stepBase);
		const top = Math.ceil(stepBase / step) * step;
		const numTicks = 5;
		const actualStep = top / (numTicks - 1);
		const ticks = Array.from({ length: numTicks }, (_, i) => Math.round(i * actualStep));

		// Extend domain beyond last tick for spacing
		const extendedTop = top * 1.15;

		return {
			transformedData: transformedChartData,
			xDomain: [0, extendedTop] as [number, number],
			xTicks: ticks,
			hasData: true,
			dataLength: transformedChartData.length,
		};
	}, [chartData]);

	// Create amount range categories and assign colors
	const { amountRanges, dataWithRanges } = useMemo(() => {
		if (transformedData.length === 0) {
			return { amountRanges: [], dataWithRanges: [] };
		}

		const maxValue = Math.max(...transformedData.map(d => d.value));
		const minValue = Math.min(...transformedData.map(d => d.value));

		// Create 4 ranges
		const rangeSize = (maxValue - minValue) / 4;
		const ranges = [
			{
				name: `$${Math.round(minValue).toLocaleString()} - $${Math.round(minValue + rangeSize).toLocaleString()}`,
				min: minValue,
				max: minValue + rangeSize,
				color: colors.primary,
			},
			{
				name: `$${Math.round(minValue + rangeSize).toLocaleString()} - $${Math.round(minValue + rangeSize * 2).toLocaleString()}`,
				min: minValue + rangeSize,
				max: minValue + rangeSize * 2,
				color: colors.chartGradient2,
			},
			{
				name: `$${Math.round(minValue + rangeSize * 2).toLocaleString()} - $${Math.round(minValue + rangeSize * 3).toLocaleString()}`,
				min: minValue + rangeSize * 2,
				max: minValue + rangeSize * 3,
				color: colors.oceanBlue,
			},
			{
				name: `$${Math.round(minValue + rangeSize * 3).toLocaleString()} - $${Math.round(maxValue).toLocaleString()}`,
				min: minValue + rangeSize * 3,
				max: maxValue,
				color: colors.success,
			},
		];

		// Assign range to each data point
		const dataWithRange = transformedData.map(item => {
			const range = ranges.find(r => item.value >= r.min && item.value <= r.max) || ranges[0];
			return {
				...item,
				range: range.name,
			};
		});

		return { amountRanges: ranges, dataWithRanges: dataWithRange };
	}, [transformedData]);

	// Filter visible data based on disabled ranges
	const visibleChartData = useMemo(() => {
		return dataWithRanges.filter((item) => !disabledRanges.has(item.range));
	}, [dataWithRanges, disabledRanges]);

	// Recalculate hasData based on visible data
	const hasVisibleData = visibleChartData.length > 0;

	// Prepare table data for ChartWithTableView
	const tableData: TableData = useMemo(() => ({
		columns: [
			{ key: 'name', label: 'Date', align: 'left' as const },
			{
				key: 'value',
				label: 'Collection Amount',
				align: 'right' as const,
				render: (value: number) => `$${value.toLocaleString()}`
			},
		],
		rows: transformedData,
	}), [transformedData]);

	// Calculate dynamic height based on number of data points
	const chartHeight = useMemo(() => {
		if (isFullScreen) {
			return window.innerHeight - 150; // Fullscreen: use viewport height minus header/padding
		}
		const containerHeight = height - 110; // Reduced space for compact legends
		return containerHeight; // Always use full container height
	}, [height, isFullScreen]);

	// Calculate chart width
	const chartWidth = width - 20; // Account for padding

	// SVG gradient definition
	const gradientId = 'dailyCollectionGradient';

	return (
		<ChartWithTableView
			title={title}
			tableData={tableData}
			showMenu={false}
			isTableModalOpen={isTableModalOpen}
			setIsTableModalOpen={setIsTableModalOpen}
		>
			<Show when={isLoading}>
				<Box
					width="100%"
					height={`${height}px`}
					borderRadius="md"
					borderWidth="1px"
					borderColor={'gray.400'}
					p={3}
					pl={2}
					_dark={{ bg: 'darkModeBgColor' }}
					_light={{ bg: 'white' }}
					overflow="hidden"
					display="flex"
					flexDirection="column"
					{...rest}
				>
					{/* Title and Filter Skeleton */}
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={4} mt={2} ml={4} mr={4}>
						<Skeleton
							height={5}
							width="200px"
							borderRadius="md"
							_dark={{ bg: 'gray.700' }}
							_light={{ bg: 'gray.200' }}
						/>
						<Skeleton
							height={8}
							width="130px"
							borderRadius="md"
							_dark={{ bg: 'gray.700' }}
							_light={{ bg: 'gray.200' }}
						/>
					</Box>

					{/* Chart Area */}
					<Box
						flex="1"
						px={4}
						py={4}
						display="flex"
						flexDirection="column"
						justifyContent="space-evenly"
						gap={{ base: 2, sm: 2, md: 3 }}
						width="100%"
						position="relative"
					>
						{Array.from({ length: 8 }, (_, index) => (
							<Box
								// biome-ignore lint/suspicious/noArrayIndexKey: skeleton
								key={index}
								display="flex"
								alignItems="center"
								gap={3}
								width="100%"
							>
								{/* Y-axis label */}
								<Skeleton
									height={4}
									width="80px"
									borderRadius="sm"
									flexShrink={0}
									_dark={{ bg: 'gray.700' }}
									_light={{ bg: 'gray.200' }}
								/>
								{/* Horizontal bar */}
								<Skeleton
									height={6}
									width={`${35 + (index % 5) * 12}%`}
									borderRadius="md"
									_dark={{ bg: 'gray.700' }}
									_light={{ bg: 'gray.200' }}
								/>
							</Box>
						))}
					</Box>

					{/* X-axis labels skeleton */}
					<Box display="flex" justifyContent="space-between" px={4} ml="80px" pb={2}>
						{Array.from({ length: 4 }, (_, index) => (
							<Skeleton
								// biome-ignore lint/suspicious/noArrayIndexKey: skeleton
								key={index}
								height={3}
								width="40px"
								borderRadius="sm"
								_dark={{ bg: 'gray.700' }}
								_light={{ bg: 'gray.200' }}
							/>
						))}
					</Box>
				</Box>
			</Show>

			<Show when={!isLoading}>
				<Box
					ref={chartContainerRef}
					width="100%"
					height={isFullScreen ? '100vh' : `${height}px`}
					borderRadius="md"
					borderWidth="1px"
					borderColor={'gray.400'}
					p={isFullScreen ? 8 : 3}
					pl={isFullScreen ? 8 : 2}
					_dark={{ bg: 'darkModeBgColor' }}
					_light={{ bg: 'white' }}
					overflow="hidden"
					display="flex"
					flexDirection="column"
					position="relative"
					{...rest}
				>
					{/* Exit Fullscreen Button */}
					<Show when={isFullScreen}>
						<FaceliftIconButton
							position="absolute"
							top={4}
							right={4}
							zIndex={1000}
							aria-label="Exit fullscreen"
							onClick={handleFullScreen}
							size="md"
							variant="solid"
							_dark={{ bg: 'gray.700', _hover: { bg: 'gray.600' }, color: 'white' }}
							_light={{ bg: 'gray.100', _hover: { bg: 'gray.200' }, color: 'gray.800' }}
						>
							<MdFullscreenExit size={20} />
						</FaceliftIconButton>
					</Show>
					<Show when={!!title}>
						<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={2} ml={4} mr={4}>
							<FaceliftText
								fontSize="md"
								fontWeight="semibold"
								_dark={{ color: 'white' }}
								_light={{ color: 'gray.800' }}
							>
								{title}
							</FaceliftText>
							<Show when={!isFullScreen}>
								<Stack direction="row" gap={2} align="center">
									<Show when={showFilters}>
										<Box width="130px">
											<FaceliftSelect<SelectItem, false>
												placeholder="Select Date Range"
												value={selectedFilter}
												onChange={handleFilterChange}
												items={chartDateFilterOptions}
												isSearchable={false}
											/>
										</Box>
									</Show>
									{/* Chart Options Menu */}
									<Show when={showTableMenu}>
										<ProfileMenu
											trigger={
												<FaceliftIconButton
													aria-label="chart options menu"
													size="sm"
													variant="ghost"
												>
													<HorizontalBarsIcon width={16} height={15} />
												</FaceliftIconButton>
											}
											isOpen={isMenuOpen}
											onOpenChange={setIsMenuOpen}
											onNavigate={handleMenuNavigate}
											menuItems={chartMenuItems}
										/>
									</Show>
								</Stack>
							</Show>
						</Box>
					</Show>
					<Show when={!hasVisibleData}>
						<Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={10}>
							<FaceliftText
								fontSize="md"
								fontWeight="semibold"
								_dark={{ color: 'gray.400' }}
								_light={{ color: 'gray.500' }}
							>
								No data found
							</FaceliftText>
						</Box>
					</Show>

					{/* Chart View */}
					<Box
						height="calc(100% - 50px)"
						overflow={isFullScreen || disableScroll ? "hidden" : "auto"}
						pb={0}
						position="relative"
						onMouseMove={handleChartMouseMove}
						onMouseLeave={handleBarMouseLeave}
					>
						{/* Custom Tooltip positioned absolutely */}
						<Show when={!!tooltipState?.show}>
							<Box
								position="absolute"
								left={`${tooltipState?.x}px`}
								top={`${tooltipState?.y}px`}
								transform="translate(-50%, -100%)"
								p={3}
								borderRadius="md"
								borderWidth="1px"
								boxShadow="lg"
								_dark={{ bg: 'darkModeBgColor', borderColor: 'gray.600' }}
								_light={{ bg: 'white', borderColor: 'gray.200' }}
								zIndex={1000}
								pointerEvents="none"
							>
								<FaceliftText
									fontSize="sm"
									fontWeight="semibold"
									mb={2}
									_dark={{ color: 'white' }}
									_light={{ color: 'gray.800' }}
								>
									{tooltipState?.name}
								</FaceliftText>
								<Stack gap={1}>
									<Stack direction="row" align="center" gap={2}>
										<svg width="8" height="8">
											<defs>
												<linearGradient id="custom-tooltip-gradient" x1="0" y1="0" x2="1" y2="0">
													<stop offset="0%" stopColor={colors.primary} stopOpacity={1} />
													<stop offset="100%" stopColor={colors.success} stopOpacity={1} />
												</linearGradient>
											</defs>
											<circle cx="4" cy="4" r="4" fill="url(#custom-tooltip-gradient)" />
										</svg>
										<FaceliftText fontSize="xs" _dark={{ color: 'gray.300' }} _light={{ color: 'gray.600' }}>
											${tooltipState?.value.toLocaleString()}
										</FaceliftText>
									</Stack>
								</Stack>
							</Box>
						</Show>

						<Box width="100%" height={`${chartHeight}px`} minWidth={disableScroll ? "100%" : { base: "600px", md: "100%", lg: "800px" }}>
							<svg width="0" height="0">
								<defs>
									<linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
										<stop offset="0%" stopColor={colors.primary} stopOpacity={1} />
										<stop offset="100%" stopColor={colors.success} stopOpacity={1} />
									</linearGradient>
								</defs>
							</svg>
							<ResponsiveContainer width="100%" height={chartHeight}>
								<BarChart
									data={visibleChartData}
									layout="vertical"
									margin={{ top: 20, right: 10, left: 5, bottom: 10 }}
									barCategoryGap="10%"
								>
									<Show when={hasVisibleData}>
										<CartesianGrid strokeDasharray="0" horizontal={false} stroke={colors.robotGrey} syncWithTicks={true} />
									</Show>
									<Show when={hasVisibleData}>
										<XAxis
											type="number"
											axisLine={true}
											tickLine={false}
											tickMargin={5}
											tick={{
												style: {
													fill: isDark ? colors.lightGray : colors.black,
													fontSize: '12px',
													fontWeight: 500,
													lineHeight: '100%',
													letterSpacing: '1%',
													textAlign: 'center',
												},
											}}
											tickFormatter={(value) => `$${formatChartNumber(value)}`}
											domain={xDomain}
											ticks={xTicks}
											padding={{ left: 0, right: 0 }}
										/>
									</Show>
									<Show when={hasVisibleData}>
										<YAxis
											type="category"
											dataKey="name"
											axisLine={true}
											tickLine={false}
											tickMargin={5}
											width={90}
											tick={{
												style: {
													fill: isDark ? colors.lightGray : colors.black,
													fontSize: '10px',
													fontWeight: 500,
													lineHeight: '100%',
													textAlign: 'right',
												},
											}}
											interval={0}
										/>
									</Show>
									<Show when={hasVisibleData}>
										<Bar
											dataKey="value"
											fill={`url(#${gradientId})`}
											radius={[0, 4, 4, 0]}
											maxBarSize={20}
											isAnimationActive={false}
											onMouseEnter={handleBarMouseEnter}
											onMouseLeave={handleBarMouseLeave}
										/>
									</Show>
								</BarChart>
							</ResponsiveContainer>

							{/* Legend Container - Amount Ranges */}
							<Box
								display="flex"
								flexWrap="wrap"
								justifyContent="center"
								gap={2}
								px={2}
								py={2}
								mt={1}
								mb={2}
							>
								{amountRanges.map((range) => {
									const isDisabled = disabledRanges.has(range.name);
									const legendGradientId = `daily-legend-${range.name.replace(/[\s$,-]/g, '')}`;
									return (
										<Stack
											key={range.name}
											direction="row"
											align="center"
											gap={1.5}
											cursor="pointer"
											onClick={() => handleLegendClick(range.name)}
											opacity={isDisabled ? 0.4 : 1}
											transition="opacity 0.2s"
											_hover={{ opacity: isDisabled ? 0.6 : 0.8 }}
										>
											<svg width="8" height="8">
												<defs>
													<linearGradient id={legendGradientId} x1="0" y1="0" x2="1" y2="0">
														<stop offset="0%" stopColor={colors.primary} stopOpacity={1} />
														<stop offset="100%" stopColor={colors.success} stopOpacity={1} />
													</linearGradient>
												</defs>
												<circle cx="4" cy="4" r="4" fill={`url(#${legendGradientId})`} />
											</svg>
											<FaceliftText
												fontSize="xs"
												fontWeight="medium"
												_dark={{ color: 'gray.200' }}
												_light={{ color: 'gray.800' }}
												textDecoration={isDisabled ? 'line-through' : 'none'}
											>
												{range.name}
											</FaceliftText>
										</Stack>
									);
								})}
							</Box>
						</Box>
					</Box>
				</Box>
			</Show>
			<FaceliftDialog
				title="Select Period"
				open={isDateFilterModalOpen}
				onOpenChange={(open) => { }}
				sx={{
					maxWidth: 400,
					maxHeight: 400,
				}}
				footer={
					<>
						<FaceliftButton
							width="100px"
							bg="transparent"
							color={{ _light: 'gray.600', _dark: 'gray.100' }}
							border="1px solid"
							borderColor={{ _light: 'gray.600', _dark: 'gray.100' }}
							onClick={() => {
								setIsDateFilterModalOpen(false);
							}}
						>
							Close
						</FaceliftButton>
						<FaceliftButton
							width="100px"
							onClick={() => {
								if (customDateRange.startDate && customDateRange.endDate) {
									// Convert DateRange format (startDate/endDate) to chart format (start/end)
									// DateRange uses MM/DD/YYYY format, convert to YYYY-MM-DD
									const parsedStartDate = dayjs(customDateRange.startDate, 'MM/DD/YYYY');
									const parsedEndDate = dayjs(customDateRange.endDate, 'MM/DD/YYYY');

									// Validate dates are valid before using
									if (parsedStartDate.isValid() && parsedEndDate.isValid()) {
										setDateRange({
											start: parsedStartDate.format('YYYY-MM-DD'),
											end: parsedEndDate.format('YYYY-MM-DD')
										});
										setIsDateFilterModalOpen(false);
									}
								}
							}}
							disabled={!customDateRange.startDate || !customDateRange.endDate}
						>
							Apply
						</FaceliftButton>
					</>
				}
			>
				<FaceliftDatePicker
					isRange={true}
					range={customDateRange}
					onRangeChange={setCustomDateRange}
					placeholder="Choose period..."
				/>
			</FaceliftDialog>
		</ChartWithTableView>
	);
};

export default DailyCollectionBarChart;

